import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SearchBarComponent } from '@components/search-bar-component/search-bar.component';

import { CategoriesService } from '@services/categories/categories.service';
import {
  Category,
  CategoryDeleteConflictError,
  CategoryNode,
} from '@services/categories/interfaces/categories.interfaces';
import { ModalService } from '@services/components/ModalService/modal.service';

import {
  CategoryDeleteModalComponent,
  CategoryDeleteModalData,
} from './category-delete-modal/category-delete-modal.component';
import {
  CategoryFormModalComponent,
  CategoryFormModalData,
  CategoryFormModalResult,
} from './category-form-modal/category-form-modal.component';

import { CDN_CATEGORIES_URL } from '@constants/constants';
import { MERCHANDISING_CATEGORY_ID } from '@constants/merchandising.constants';

interface CategoryRow {
  kind: 'category';
  category: CategoryNode;
  depth: number;
}

interface AddChildRow {
  kind: 'add-child';
  parent: Category;
}

type Row = CategoryRow | AddChildRow;

@Component({
  selector: 'app-manage-categories',
  standalone: true,
  imports: [CommonModule, SearchBarComponent],
  templateUrl: './manage-categories.html',
  styleUrl: './manage-categories.css',
})
export class ManageCategoriesComponent implements OnInit {
  private readonly categoriesService = inject(CategoriesService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly categories = signal<Category[]>([]);
  protected readonly tree = signal<CategoryNode[]>([]);
  protected readonly isLoading = signal<boolean>(true);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly searchTerm = signal<string>('');
  protected trackByRow = (_: number, row: Row): string =>
    row.kind === 'category' ? `c-${row.category.id}` : `a-${row.parent.id}`;

  protected readonly rows = computed<Row[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const roots = this.tree();
    const out: Row[] = [];

    const matches = (node: CategoryNode): boolean =>
      !term || node.name.toLowerCase().includes(term);

    roots.forEach((root) => {
      const rootMatches = matches(root);
      const matchingChildren = root.children.filter(matches);

      if (term && !rootMatches && matchingChildren.length === 0) {
        return;
      }

      out.push({ kind: 'category', category: root, depth: 0 });
      matchingChildren.forEach((child) => {
        out.push({ kind: 'category', category: child, depth: 1 });
      });

      if (!term) {
        out.push({ kind: 'add-child', parent: root });
      }
    });

    return out;
  });

  protected readonly isEmpty = computed(
    () => !this.isLoading() && this.categories().length === 0,
  );

  public ngOnInit(): void {
    this.loadCategories();
  }

  public onSearch(query: string): void {
    this.searchTerm.set(query);
  }

  public onAdd(): void {
    this.openForm(undefined, undefined);
  }

  public isProtectedCategory(category: Category): boolean {
    return category.id === MERCHANDISING_CATEGORY_ID;
  }

  public onAddChild(parent: Category): void {
    this.openForm(undefined, parent);
  }

  public onEdit(category: Category): void {
    this.openForm(category, undefined);
  }

  public onDelete(node: CategoryNode): void {
    const data: CategoryDeleteModalData = {
      categoryName: node.name,
      hasChildren: node.children.length > 0,
    };

    this.modalService
      .openModal<boolean, CategoryDeleteModalData>({
        title: 'Eliminar categoría',
        size: 'sm',
        component: CategoryDeleteModalComponent,
        data,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.reason === 'confirm' && event.data === true) {
          this.deleteCategory(node);
        }
      });
  }

  protected getCategoryImage(category: Category): string | null {
    return category.imageUrl ? `${CDN_CATEGORIES_URL}${category.imageUrl}` : null;
  }

  protected getParentName(category: Category): string {
    if (!category.parentId) {
      return '-';
    }
    return this.categories().find((c) => c.id === category.parentId)?.name ?? '-';
  }

  private openForm(category: Category | undefined, presetParent: Category | undefined): void {
    const data: CategoryFormModalData = {
      category,
      allCategories: this.categories(),
      presetParent,
    };

    const title = category
      ? `Editar ${category.parentId ? 'Subcategoría' : 'Categoría'} ${category.name}`
      : presetParent
        ? `Nueva Subcategoría para ${presetParent.name}`
        : 'Nueva Categoría';

    this.modalService
      .openModal<CategoryFormModalResult, CategoryFormModalData>({
        title,
        component: CategoryFormModalComponent,
        size: 'md',
        data,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.reason !== 'confirm' || !event.data) {
          return;
        }
        this.persistCategory(event.data);
      });
  }

  private persistCategory(result: CategoryFormModalResult): void {
    const formData = this.buildCategoryFormData(result);

    const request$ =
      result.mode === 'create'
        ? this.categoriesService.createCategory(formData)
        : this.categoriesService.updateCategory(result.id, formData);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadCategories(),
      error: (err: HttpErrorResponse) => this.handlePersistError(err),
    });
  }

  private buildCategoryFormData(result: CategoryFormModalResult): FormData {
    const formData = new FormData();
    const payload = result.payload as Record<string, unknown>;
    const keyMap: Record<string, string> = { parentId: 'parent_id' };
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      formData.append(keyMap[key] ?? key, String(value));
    });
    if (result.imageFile) {
      formData.append('image', result.imageFile);
    }
    return formData;
  }

  private handlePersistError(err: HttpErrorResponse): void {
    const status = err.status;
    const message = err.error?.message as string | undefined;
    if (status === 409 || /existe/i.test(message ?? '')) {
      this.errorMessage.set(message ?? 'Ya existe una categoría con ese nombre');
    } else {
      this.errorMessage.set(message ?? 'Hubo un error al guardar la categoría');
    }
    setTimeout(() => this.errorMessage.set(null), 5000);
  }

  private deleteCategory(node: CategoryNode): void {
    this.categoriesService
      .deleteCategory(node.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadCategories(),
        error: (err: HttpErrorResponse) => this.handleDeleteError(err, node),
      });
  }

  private handleDeleteError(err: HttpErrorResponse, node: CategoryNode): void {
    const conflict = err.error as CategoryDeleteConflictError | undefined;
    const blockingProducts =
      conflict?.products ?? conflict?.extraData?.products ?? [];
    if (blockingProducts.length > 0) {
      const data: CategoryDeleteModalData = {
        categoryName: node.name,
        hasChildren: node.children.length > 0,
        blockingProducts,
      };
      this.modalService
        .openModal<boolean, CategoryDeleteModalData>({
          title: 'No se puede eliminar',
          size: 'sm',
          component: CategoryDeleteModalComponent,
          data,
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
      return;
    }
    this.errorMessage.set(conflict?.message ?? 'Hubo un error al eliminar la categoría');
    setTimeout(() => this.errorMessage.set(null), 5000);
  }

  private loadCategories(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.categoriesService
      .getCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.categories.set(data);
          this.tree.set(this.categoriesService.buildTree(data));
          this.isLoading.set(false);
        },
        error: () => {
          this.categories.set([]);
          this.tree.set([]);
          this.isLoading.set(false);
          this.errorMessage.set(
            'No se pudieron cargar las categorías. Intenta nuevamente.',
          );
        },
      });
  }
}
